// src/utils/priceFormatter.js
// 💰 Utilitários para formatação de preços
// Criado em: 16/03/2026
// Versão: 1.0

/**
 * Formata um valor para moeda brasileira (R$)
 * @param {number} value - Valor a ser formatado
 * @param {boolean} showSymbol - Mostrar símbolo R$
 * @returns {string} Valor formatado (ex: R$ 1.234,56)
 */
export const formatBRL = (value, showSymbol = true) => {
  if (value === null || value === undefined) return showSymbol ? 'R$ 0,00' : '0,00';
  
  const formatter = new Intl.NumberFormat('pt-BR', {
    style: showSymbol ? 'currency' : 'decimal',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return formatter.format(value);
};

/**
 * Formata um valor para exibição em cards (formato compacto)
 * @param {number} value - Valor a ser formatado
 * @returns {string} Valor formatado (ex: R$ 1,2 mil | R$ 1,5 Mi)
 */
export const formatCompactBRL = (value) => {
  if (value === null || value === undefined) return 'R$ 0';
  
  const absValue = Math.abs(value);
  
  if (absValue >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1)} Mi`;
  }
  if (absValue >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(1)} mil`;
  }
  
  return `R$ ${value.toFixed(0)}`;
};

/**
 * Converte string para número (remove formatação)
 * @param {string} value - Valor formatado (ex: "R$ 1.234,56")
 * @returns {number} Número
 */
export const parseBRL = (value) => {
  if (!value) return 0;
  
  const cleaned = value
    .replace('R$', '')
    .replace(/\./g, '')
    .replace(',', '.')
    .trim();
    
  return parseFloat(cleaned) || 0;
};

/**
 * Formata preço de plano com período
 * @param {number} price - Preço mensal
 * @param {string} period - Período ('month', 'year')
 * @returns {string} Preço formatado com período
 */
export const formatPlanPrice = (price, period = 'month') => {
  const formatted = formatBRL(price);
  
  if (period === 'year') {
    return `${formatted}/ano`;
  }
  
  return `${formatted}/mês`;
};

/**
 * Calcula preço anual com desconto (opcional)
 * @param {number} monthlyPrice - Preço mensal
 * @param {number} discountPercent - Percentual de desconto anual
 * @returns {Object} Preços anual e economia
 */
export const calculateYearlyPrice = (monthlyPrice, discountPercent = 10) => {
  const yearlyPrice = monthlyPrice * 12;
  const discountedPrice = yearlyPrice * (1 - discountPercent / 100);
  const savings = yearlyPrice - discountedPrice;
  
  return {
    monthly: monthlyPrice,
    yearly: yearlyPrice,
    discounted: discountedPrice,
    savings: savings,
    formatted: {
      monthly: formatBRL(monthlyPrice),
      yearly: formatBRL(yearlyPrice),
      discounted: formatBRL(discountedPrice),
      savings: formatBRL(savings)
    }
  };
};

/**
 * Compara dois preços e retorna o mais vantajoso
 * @param {Array} prices - Array de preços
 * @returns {Object} Informações do melhor preço
 */
export const getBestPrice = (prices) => {
  if (!prices || prices.length === 0) return null;
  
  const best = prices.reduce((min, p) => p.value < min.value ? p : min, prices[0]);
  
  return {
    ...best,
    formatted: formatBRL(best.value),
    isBest: true
  };
};

/**
 * Formata preço para o gateway de pagamento
 * @param {number} price - Preço em reais
 * @returns {number} Preço em centavos (para gateway)
 */
export const priceToGateway = (price) => {
  return Math.round(price * 100);
};

/**
 * Converte preço do gateway (centavos) para reais
 * @param {number} cents - Preço em centavos
 * @returns {number} Preço em reais
 */
export const priceFromGateway = (cents) => {
  return cents / 100;
};

// ✅ Exportação única para facilitar importação
export default {
  formatBRL,
  formatCompactBRL,
  parseBRL,
  formatPlanPrice,
  calculateYearlyPrice,
  getBestPrice,
  priceToGateway,
  priceFromGateway
};